import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import type {
  FileUploadOptions,
  FileUploadResult,
  UploadedFile,
} from "../types";
import { useFileStorageValidator } from "./useFileStorageValidator";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";

export const useFileUpload = ({
  client,
  fieldName,
  validateOptions,
  onUploadSuccess,
  onUploadError,
}: FileUploadOptions): FileUploadResult => {
  const operationProgress = useAsyncOperationProgress();
  const { validateFiles } = useFileStorageValidator();

  const uploadFiles = useCallback(
    async (
      files: File[],
    ): Promise<{
      failedUploads: { file: File; error: unknown }[];
      uploadedCount: number;
      validationError?: string;
    }> => {
      if (files.length === 0) {
        return { failedUploads: [], uploadedCount: 0 };
      }

      const validationError = await validateFiles(files, validateOptions);
      if (validationError) {
        return {
          failedUploads: [],
          uploadedCount: 0,
          validationError,
        };
      }

      const items = files.map((file, index) => ({
        id: `${file.name}-${index}`,
        label: file.name,
      }));

      operationProgress.initialize(items);
      operationProgress.startProcessing();

      const failedUploads: { file: File; error: unknown }[] = [];
      let uploadedCount = 0;

      try {
        for (let i = 0; i < files.length; i++) {
          // isCancelling is not memoized, but safe to access directly
          // eslint-disable-next-line react-hooks/exhaustive-deps
          if (operationProgress.isCancelling()) {
            break;
          }

          const file = files[i];
          const uniqueId = `${file.name}-${i}`;
          operationProgress.setCurrentItem(file.name);

          let uploaded: UploadedFile;
          try {
            uploaded = await client.upload(fieldName, file, {
              skipValidation: true,
            });
          } catch (error) {
            operationProgress.markItemProcessed(uniqueId, false);
            failedUploads.push({ file, error });
            onUploadError?.(file, error);
            continue;
          }
          operationProgress.markItemProcessed(uniqueId, true);
          uploadedCount += 1;

          try {
            onUploadSuccess?.(file, uploaded);
          } catch (error) {
            // Plik jest już na serwerze: błąd post-processingu (np. odpowiedź complete o złym kształcie)
            // nie jest nieudanym uploadem — nie trafia do failedUploads i nie liczy się podwójnie.
            // To błąd kontraktu, nie użytkownika: do Sentry, bo samo console.error go omija.
            console.error("Upload post-processing failed:", error);
            Sentry.captureException(error);
          }
        }
      } finally {
        operationProgress.complete();
      }

      return { failedUploads, uploadedCount };
    },
    // operationProgress is intentionally omitted - isCancelling is not memoized but safe to access
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      client,
      fieldName,
      validateOptions,
      onUploadSuccess,
      onUploadError,
      validateFiles,
    ],
  );

  return { uploadFiles, operationProgress };
};
